import { ComponentClass } from 'react';
import { createLazyComponent, LazyAction, LazyImport, LazyModule } from 'owa-bundling';
import {
    CategorySearchSuggestion,
    PeopleSuggestion,
    PrivateDistributionListSuggestion,
    SuggestionSet,
    FlaggedToken,
    SubstrateSearchSuggestionPersonSegment,
    SubstrateSearchSuggestionTextSegment,
} from 'owa-search-service';
export { ComponentClass };

// Register mutators
import './mutators/currentSuggestionsMutators';
import './mutators/initializeScenarioStoreMutator';
import './mutators/setIsSuggestionsDropdownVisibleMutator';
import './mutators/setSearchTextMutator';
import './mutators/addSuggestionPillInternalMutator';
import './mutators/setLatestTraceIdMutator';
import './mutators/searchBoxHasFocusMutators';
export { default as setLatestRenderedQFTraceId } from './mutators/setLatestRenderedQFTraceId';
import './orchestrators/endSearchConversationOrchestrator';
import './mutators/startSearchMutator';

// Register orchestrators
import './orchestrators/onSearchInputFocusedOrchestrator';
import './orchestrators/onEnterPressedSearchInputOrchestrator';
import './orchestrators/onSearchTextChangedOrchestrator';
import './orchestrators/startSearchSessionOrchestrator';
import './orchestrators/registerAdditionalQuickActionsOrchestrator';
import './orchestrators/addSuggestionPillOrchestrator';
import './mutators/endSearchConversationMutator';

const lazyModule = new LazyModule(() =>
    import(/* webpackChunkName: "Search", webpackPrefetch: true */ './lazyIndex')
);

// Required for actions that depend on them.
export {
    CategorySearchSuggestion,
    FlaggedToken,
    PeopleSuggestion,
    PrivateDistributionListSuggestion,
    SubstrateSearchSuggestionPersonSegment,
    SubstrateSearchSuggestionTextSegment,
    SuggestionSet,
};

// Export selectors, interfaces, enums, constants
export { default as getHighlightTerms } from './selectors/getHighlightTerms';
export { default as getScenarioStore } from './selectors/getScenarioStore';
export { default as SearchStore } from './store/schema/SearchStore';
export { default as searchStore, createDefaultSearchStore, getStore } from './store/store';
export { START_MATCH_DELIMITER, END_MATCH_DELIMITER, SEARCHBOX_ID } from './searchConstants';
export { default as SearchScenarioId } from './store/schema/SearchScenarioId';

// Export actions
export {
    addSuggestionPill,
    changeSelectedSuggestionViaKeyboard,
    clearSearchBox,
    clearSegments,
    downloadFileSuggestion,
    executeCommand,
    exitSearch,
    getSuggestions,
    searchBoxBlurred,
    searchSessionEnded,
    searchSessionStarted,
    setCurrentSuggestions,
    setDisplayedQFRequestTime,
    shareFileSuggestion,
    startSearch,
    setLatestTraceId,
    suggestionSelected,
    suggestionSecondaryActionSelected,
    feedbackSubmitted,
    onSearchTextChanged,
    startSearchSession,
    registerAdditionalQuickActions,
    setSearchTextForSuggestion,
    endSearchConversation,
} from './actions/publicActions';

// Export utils
export {
    isGfEnabled,
    shouldSendPillDisplayText,
    getXClientFlightsHeaderValue,
} from './utils/guidedFormulationExperimentUtilities';

// Export lazy loaded actions
//VSO#35181 remove clearSearchBox from lazy, is exported as an action above
export const lazyClearSearchBox = new LazyImport(lazyModule, m => m.clearSearchBox);
export const lazyEndSearchSession = new LazyImport(lazyModule, m => m.endSearchSession);
export const lazyOnDownArrowPressedSearchInput = new LazyImport(
    lazyModule,
    m => m.onDownArrowPressedSearchInput
);
export const lazyOnBackspacePressedSearchInput = new LazyImport(
    lazyModule,
    m => m.onBackspacePressedSearchInput
);
export const lazyOnLeftArrowPressedSearchInput = new LazyImport(
    lazyModule,
    m => m.onLeftArrowPressedSearchInput
);
export const lazyOnRightArrowPressedSearchInput = new LazyImport(
    lazyModule,
    m => m.onRightArrowPressedSearchInput
);
export const lazyOnUpArrowPressedSearchInput = new LazyImport(
    lazyModule,
    m => m.onUpArrowPressedSearchInput
);
export const lazyRemoveSuggestionPillFromSearchBox = new LazyImport(
    lazyModule,
    m => m.removeSuggestionPillFromSearchBox
);
export const lazySetLatestQFRequestId = new LazyImport(lazyModule, m => m.setLatestQFRequestId);
export const lazySetIsSuggestionsDropdownVisible = new LazyImport(
    lazyModule,
    m => m.setIsSuggestionsDropdownVisible
);
export const lazySetSuggestionSegments = new LazyImport(lazyModule, m => m.setSuggestionSegments);
export const lazyUpdateIsSuggestionSetComplete = new LazyImport(
    lazyModule,
    m => m.updateIsSuggestionSetComplete
);

// Export mutators
export const lazySetSpellerData = new LazyAction(lazyModule, m => m.setSpellerData);

// Export components
export { default as SearchBox } from './components/SearchBox';

// Export lazy loaded components
export const PersonaPill = createLazyComponent(lazyModule, m => m.PersonaPill);
export const PrivateDistributionListPill = createLazyComponent(
    lazyModule,
    m => m.PrivateDistributionListPill
);
export const SearchBoxDropdown = createLazyComponent(lazyModule, m => m.SearchBoxDropdown);
export const SearchBoxPillWell = createLazyComponent(lazyModule, m => m.SearchBoxPillWell);
export const SuggestionsCallout = createLazyComponent(lazyModule, m => m.SuggestionsCallout);

// Export selectors
export { default as getSearchBoxWidth } from './selectors/getSearchBoxWidth';
export { default as queryIncludesKQLSuggestion } from './selectors/queryIncludesKQLSuggestion';
export const lazyGetSuggestionAtIndex = new LazyImport(lazyModule, m => m.getSuggestionAtIndex);
export const lazyIsInSearchMode = new LazyImport(lazyModule, m => m.isInSearchMode);
export const lazyIsQFRequestIdEqualToLatest = new LazyImport(
    lazyModule,
    m => m.isQFRequestIdEqualToLatest
);
export const lazyIsSearchBoxEmpty = new LazyImport(lazyModule, m => m.isSearchBoxEmpty);
